"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.sellDayPassSchema = exports.updateDayPassOfferSchema = exports.dayPassOfferSchema = void 0;
const zod_1 = require("zod");
/**
 * Boot Camp offers and passes — SPEC §6.1 "Boot Camp Offer": a flat-rate day
 * pass, fixed price, unlimited play for a window (e.g. until closing).
 *
 * The schema for this has existed since the first migration (`BootCampOffer`,
 * `BootCampPass`, `Session.bootCampPassId`) and so has the owner's settings
 * toggle, `enableBootCampOffer`. Nothing ever read any of it, so switching the
 * toggle on changed nothing anywhere — which is the worst kind of setting.
 *
 * **How a pass stops the meter, and why it is not a discount.** Starting a
 * session on a pass snapshots a rate of ZERO onto the session row, the same
 * way every session snapshots the device's rates at start (SPEC §4.1). The
 * billing functions then bill zero without knowing passes exist. Two things
 * follow from that, and both are the point:
 *
 *  - a price edit, a discount rule change or a rounding change can never
 *    retroactively put a charge on a sitting that was sold as unlimited;
 *  - the pass is charged ONCE, when it is sold, onto the tab — so the money
 *    appears in the same place as everything else the party owes, and the
 *    §5.7 reports need no special case to find it.
 *
 * Money crosses the wire as decimal strings of millidinars.
 */
/** Whole IQD from the operator; the server converts to millidinars. */
const priceIqd = zod_1.z
    .number()
    .int('Prices are whole dinars — IQD has no useful sub-unit.')
    .min(0, 'A price cannot be negative.')
    .max(1_000_000_000);
/* -------------------------------------------------------------------------- */
/* Offers — what the venue sells                                              */
/* -------------------------------------------------------------------------- */
/**
 * `durationMinutes` null means "until the venue's closing time", which is what
 * a day pass usually is. It is nullable rather than defaulted because the two
 * answer differently when a venue changes its closing time mid-pass: a fixed
 * window is a promise of N minutes, and an open one is a promise about the day.
 */
exports.dayPassOfferSchema = zod_1.z.object({
    name: zod_1.z.string().trim().min(1, 'Give the offer a name.').max(120),
    priceIqd,
    durationMinutes: zod_1.z
        .number()
        .int()
        .min(1)
        .max(24 * 60)
        .nullable()
        .optional(),
    isActive: zod_1.z.boolean().optional(),
});
/**
 * Declared field by field rather than `.partial()`.
 *
 * zod 4 keeps `.default()` values through `.partial()`, so a partial built
 * from the create schema would silently reinstate defaults for fields the
 * caller never sent — an edit that only renamed an offer would also reset its
 * price. Same reason `updateProductSchema` is written out longhand.
 */
exports.updateDayPassOfferSchema = zod_1.z.object({
    name: zod_1.z.string().trim().min(1, 'Give the offer a name.').max(120).optional(),
    priceIqd: priceIqd.optional(),
    durationMinutes: zod_1.z
        .number()
        .int()
        .min(1)
        .max(24 * 60)
        .nullable()
        .optional(),
    isActive: zod_1.z.boolean().optional(),
});
/* -------------------------------------------------------------------------- */
/* Passes — what a customer holds                                             */
/* -------------------------------------------------------------------------- */
/**
 * Sell a pass. Exactly one of `customerName` / `tabId` / `customerAccountId`
 * names who holds it, mirroring `startSessionSchema` — a pass is bought by a
 * party, and a party in this system is a tab.
 *
 * There is no `priceIqd` here on purpose. The price is read from the offer
 * server-side, so a hand-edited request cannot buy a day of unlimited play for
 * one dinar, and so the sale is always reconcilable against the offer list.
 */
exports.sellDayPassSchema = zod_1.z
    .object({
    offerId: zod_1.z.string().min(1),
    customerName: zod_1.z.string().trim().min(1, 'Customer name is required.').max(120).optional(),
    tabId: zod_1.z.string().min(1).optional(),
    customerAccountId: zod_1.z.string().min(1).optional(),
})
    .refine((value) => Boolean(value.customerName ?? value.tabId ?? value.customerAccountId), {
    message: 'Give a customer name, or pick an open tab.',
    path: ['customerName'],
})
    .refine((value) => !(value.customerName && value.tabId), {
    message: 'Choose either a new name or an existing tab, not both.',
    path: ['tabId'],
});
//# sourceMappingURL=day-passes.js.map